'use client';
import Link from 'next/link';
import ShimmerImage from '@/components/ui/ShimmerImage';
import {
  Popover,
  PopoverButton,
  PopoverPanel,
  Transition,
  PopoverGroup,
} from '@headlessui/react';
import React, {
  memo,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { Autoplay, Pagination } from 'swiper/modules';
import { Swiper, SwiperSlide } from 'swiper/react';
import { chunks } from '@/lib/utils/utils';
import 'swiper/css';
import 'swiper/css/effect-coverflow';
import 'swiper/css/navigation';
import { useGetGoldenCompanyList } from '@/features/company';
import { useMediaQuery } from '@/hooks/useMediaQuery';

type GoldenMemberJob = {
  code: string;
  title: string;
};

export type GoldenMember = {
  id: number | string;
  alias?: string;
  logo: string;
  name: string;
  works?: GoldenMemberJob[];
};

const HOVER_OPEN_DELAY = 80;
const PANEL_WIDTH = 250;

const GoldenCompanyItem = memo(function GoldenCompanyItem({
  company,
}: {
  company: GoldenMember;
}) {
  const [hover, setHover] = useState<{ open: boolean; side: 'left' | 'right' }>(
    { open: false, side: 'left' }
  );
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const clearTimer = () => {
    if (timer.current) {
      clearTimeout(timer.current);
      timer.current = null;
    }
  };

  useEffect(() => clearTimer, []);

  const handleEnter = (e: React.MouseEvent<HTMLElement>) => {
    // Measure once, inside the event handler, so opening costs a single render.
    const rect = e.currentTarget.getBoundingClientRect();
    const side =
      rect.right + PANEL_WIDTH > window.innerWidth ? 'right' : 'left';
    clearTimer();
    timer.current = setTimeout(
      () => setHover({ open: true, side }),
      HOVER_OPEN_DELAY
    );
  };

  const handleLeave = () => {
    clearTimer();
    setHover((prev) => (prev.open ? { ...prev, open: false } : prev));
  };

  const href = `/company/${company.alias || company.id}`;
  const isRight = hover.side === 'right';

  return (
    <Popover className="relative">
      <div onMouseEnter={handleEnter} onMouseLeave={handleLeave}>
        <PopoverButton
          as={Link}
          href={href}
          prefetch={false}
          className="col-span-1 rounded-lg bg-white w-full aspect-square justify-center items-center flex px-[1px] border border-transparent transition-all duration-200 ease-in-out hover:border-primary-border relative overflow-hidden"
          style={{ boxShadow: '0px 0px 40px #0000001A' }}
        >
          <ShimmerImage
            src={company.logo}
            alt={company.name}
            objectID={Number(company.id)}
            width={64}
            height={32}
          />
          {hover.open && (
            <div
              className={`absolute z-51 top-1/2 -translate-y-1/2 ${isRight ? 'left-[-9px]' : 'right-[-9px]'}`}
            >
              <div
                className={`w-0 h-0 border-y-[8px] border-y-transparent ${isRight ? 'border-l-[8px] border-l-white' : 'border-r-[8px] border-r-white'}`}
              ></div>
            </div>
          )}
        </PopoverButton>
        <Transition
          show={hover.open}
          enter="transition duration-100 ease-out"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="transition duration-75 ease-out"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <PopoverPanel
            anchor={isRight ? 'left start' : 'right start'}
            className="flex flex-col bg-white w-60 [--anchor-gap:8px] !overflow-visible rounded-xl z-50 shadow-lg !mt-5"
            title={company.name}
          >
            <div className="py-3">
              <div className="px-4 pb-3 font-medium text-sm border-b border-black/10">
                {company.name}
              </div>
              <ul className="flex flex-col text-xs">
                {company.works && company.works.length > 0 ? (
                  company.works.map((job, jobIndex) => (
                    <li
                      key={jobIndex}
                      className="px-2 flex items-baseline hover:bg-primary/10 z-10"
                    >
                      <Link
                        href={`/job/_${job.code}`}
                        className="flex items-baseline p-1"
                      >
                        <span className="inline-block w-1 h-1 bg-black rounded-full mr-2 shrink-0 mt-1"></span>
                        <span className="leading-tight">{job.title}</span>
                      </Link>
                    </li>
                  ))
                ) : (
                  <li className="text-center mt-2 text-gray-400">
                    Одоогоор зар оруулаагүй байна.
                  </li>
                )}
              </ul>
            </div>
          </PopoverPanel>
        </Transition>
      </div>
    </Popover>
  );
});

const GoldenCompanies = () => {
  const { data: companies, isLoading } = useGetGoldenCompanyList();
  const matchesDesktop = useMediaQuery('(min-width: 640px)');
  // Until mounted, render both variants (CSS hides the wrong one) so the
  // server-rendered HTML contains the logos for every viewport.
  const [mounted, setMounted] = useState(false);
  useEffect(() => setMounted(true), []);
  const showMobile = !mounted || !matchesDesktop;
  const showDesktop = !mounted || matchesDesktop;

  const dividedCompanies = useMemo(
    () => chunks(companies ?? [], 10),
    [companies]
  );

  const renderMobile = useCallback(
    () => (
      <Swiper
        modules={[Pagination, Autoplay]}
        spaceBetween={12}
        slidesPerView={1}
        pagination={{ clickable: true }}
        breakpoints={{
          640: { slidesPerView: 3 },
          768: { slidesPerView: 4 },
          1024: { slidesPerView: 10 },
        }}
        className="min-w-full !pb-5"
      >
        {dividedCompanies.map((group, index) => (
          <SwiperSlide key={index}>
            <div className="grid grid-cols-5 gap-3 mb-4">
              {group.map((company, key) => (
                <Link
                  href={`/company/${company.alias || company.id}`}
                  prefetch={false}
                  key={key}
                  className="col-span-1 rounded-lg bg-white w-full aspect-square justify-center items-center flex px-[1px] border border-transparent transition-all duration-200 ease-in-out hover:border-primary-border relative shadow-md overflow-hidden"
                >
                  <ShimmerImage
                    src={company.logo}
                    alt={company.name}
                    objectID={Number(company.id)}
                    width={64}
                    height={32}
                  />
                </Link>
              ))}
            </div>
          </SwiperSlide>
        ))}
      </Swiper>
    ),
    [dividedCompanies]
  );

  return (
    <div className="w-full mt-8 pb-4 xl:pb-8">
      <div className="container mx-auto">
        <h3 className="text-sm ml-2 sm:ml-0 lg:text-lg font-medium pb-3 lg:pb-8">
          Алтан гишүүд
        </h3>
        {/* MOBILE */}
        <div className="min-w-full px-4 sm:hidden ">
          {isLoading ? (
            <div className="grid grid-cols-5 gap-3 mb-4">
              {Array.from({ length: 10 }).map((_, i) => (
                <div
                  key={i}
                  className="col-span-1 rounded-lg w-full aspect-square bg-gray-200 animate-pulse"
                />
              ))}
            </div>
          ) : (
            showMobile && renderMobile()
          )}
        </div>
        {/* DESKTOP */}
        <div className="h-full w-full">
          <PopoverGroup className="hidden sm:grid grid-cols-[repeat(auto-fit,_minmax(65px,_1fr))] gap-4">
            {isLoading
              ? Array.from({ length: 50 }).map((_, i) => (
                  <div
                    key={i}
                    className="rounded-lg w-full aspect-square bg-gray-200 animate-pulse"
                  />
                ))
              : showDesktop && (companies ?? []).length > 0
                ? companies?.map((company) => (
                    <GoldenCompanyItem key={company.id} company={company} />
                  ))
                : showDesktop &&
                  Array.from({ length: 50 }).map((_, i) => (
                    <div key={i} className="aspect-square block box-border" />
                  ))}
          </PopoverGroup>
        </div>
      </div>
    </div>
  );
};
export default GoldenCompanies;
